import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { AlertCircle, ExternalLink, IndianRupee, Loader2, Truck, User, Wallet } from 'lucide-react';
import {
  fetchGrievanceById,
  retryGrievanceRefund,
  GrievanceDetailPayload,
  GrievanceListRow,
} from '../../../services/MasterAdmin/grievanceAdminService';
import { GrievanceAttachmentChips } from './GrievanceAttachmentChips';
import { GrievanceResolutionSharesPanel } from './GrievanceResolutionSharesPanel';
import { GrievanceServiceSnapshotPanel } from './GrievanceServiceSnapshotPanel';
import { GrievancePolicyLinks } from './GrievancePolicyLinks';

const formatInr = (n: number | string | null | undefined) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const ledgerServiceType = (serviceType: string) => {
  if (serviceType === 'DOCTOR') return 'DOCTOR_INSTANT';
  return serviceType;
};

const maOrderLedgerPath = (serviceRefId: string, serviceType: string) => {
  const params = new URLSearchParams({
    serviceType: ledgerServiceType(serviceType),
    returnTo: 'grievances',
  });
  return `/master-admin/operations/order-ledger/${encodeURIComponent(serviceRefId)}?${params.toString()}`;
};

const userProfilePath = (userId: string) => `/users/${encodeURIComponent(userId)}`;

const ambulanceBookingPath = (vendorId: string, bookingId: string) =>
  `/vendors/ambulance/${encodeURIComponent(vendorId)}/booking/${encodeURIComponent(bookingId)}`;

const medicineOrderPath = (vendorId: string, orderId: string) =>
  `/vendors/medical-store/${encodeURIComponent(vendorId)}/order/${encodeURIComponent(orderId)}`;

const statusBadgeClass: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  UNDER_REVIEW: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  RESOLVED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  CLOSED: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

type Props = {
  grievanceId: string;
  onClose: () => void;
};

export const GrievanceViewModal: React.FC<Props> = ({ grievanceId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<GrievanceDetailPayload | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchGrievanceById(grievanceId);
      if (!res.success || !res.data) throw new Error('Failed to load grievance');
      setDetail(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Load failed');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [grievanceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const g = detail?.grievance;
  const isResolved = g?.status === 'RESOLVED' || g?.status === 'CLOSED';
  const hasShareSplit =
    isResolved &&
    (g.vendorSharePct != null || g.userSharePct != null || g.vedikaSharePct != null);
  const snapshot = detail?.serviceSnapshot ?? null;
  const vendorId =
    snapshot?.vendorId || g?.vendorId || detail?.vendor?.vendorId || '';
  const refundMissing =
    isResolved &&
    Number(g?.refundAmount) > 0 &&
    (g?.serviceType === 'DOCTOR' || g?.serviceType === 'AMBULANCE') &&
    !detail?.refundInitiated;

  const handleRetryRefund = async () => {
    if (!g) return;
    setRetrying(true);
    try {
      const res = await retryGrievanceRefund(g.grievanceId);
      if (!res.success) throw new Error(res.message || res.error || 'Retry refund failed');
      toast.success(res.message || 'Refund initiated.');
      await load();
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { message?: string; error?: string } } })?.response
        ?.data;
      toast.error(data?.message || data?.error || (e instanceof Error ? e.message : 'Retry failed'));
    } finally {
      setRetrying(false);
    }
  };

  const serviceDetailHref =
    g?.serviceType === 'AMBULANCE' && vendorId
      ? ambulanceBookingPath(vendorId, g.serviceRefId)
      : g?.serviceType === 'MEDICINE' && vendorId
        ? medicineOrderPath(vendorId, g.serviceRefId)
        : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4"
      onClick={onClose}
    >
      <Toaster position="top-right" />
      <div
        className="flex max-h-[min(92dvh,calc(100vh-1rem))] w-full max-w-6xl flex-col overflow-hidden rounded-t-xl bg-white shadow-xl dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 flex-col gap-3 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Grievance details</h2>
            {!loading && g && (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <p className="truncate font-mono text-xs text-gray-500 dark:text-gray-400">
                  {g.grievanceNumber}
                </p>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass[g.status] || ''}`}
                >
                  {g.status.replace(/_/g, ' ')}
                </span>
              </div>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {!loading && g && (
              <>
                <Link
                  to={maOrderLedgerPath(g.serviceRefId, g.serviceType)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  <Wallet className="h-4 w-4 shrink-0" />
                  Order ledger
                </Link>
                <Link
                  to={userProfilePath(g.userId)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  <User className="h-4 w-4 shrink-0" />
                  User profile
                </Link>
                {serviceDetailHref && (
                  <Link
                    to={serviceDetailHref}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
                  >
                    <Truck className="h-4 w-4 shrink-0" />
                    {g.serviceType === 'MEDICINE' ? 'Order' : 'Booking'}
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" />
                  </Link>
                )}
                <GrievancePolicyLinks grievanceId={grievanceId} />
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200 sm:mx-5">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <aside className="shrink-0 overflow-y-auto border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5 lg:w-[min(100%,380px)] lg:border-b-0 lg:border-r">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
              </div>
            ) : g ? (
              <GrievanceViewSidebar g={g} user={detail?.user} vendor={detail?.vendor} />
            ) : (
              <p className="text-sm text-gray-500">Grievance not found.</p>
            )}
          </aside>

          <main className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 sm:p-5">
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
              </div>
            ) : g ? (
              <>
                {hasShareSplit && <GrievanceResolutionSharesPanel g={g} />}

                {refundMissing && (
                  <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200 sm:flex-row sm:items-center sm:justify-between">
                    <p>
                      Grievance is {g.status}, but the Razorpay refund of {formatInr(g.refundAmount)}{' '}
                      was not created. Retry to initiate it now.
                    </p>
                    <button
                      type="button"
                      disabled={retrying}
                      onClick={() => void handleRetryRefund()}
                      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-amber-700 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-50"
                    >
                      {retrying ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <IndianRupee className="h-4 w-4" />
                      )}
                      Retry refund
                    </button>
                  </div>
                )}

                {detail?.refundInitiated && detail.refund && (
                  <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300">
                    Refund {formatInr(detail.refund.amount)} recorded ({detail.refund.status}
                    {detail.refund.razorpayRefundId ? ` · ${detail.refund.razorpayRefundId}` : ''}).
                  </p>
                )}

                {isResolved && !hasShareSplit && (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
                    This grievance is marked {g.status} but no share split was recorded.
                  </p>
                )}

                <section>
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Grievance
                  </h3>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">{g.title}</p>
                  {g.description ? (
                    <p className="mt-2 whitespace-pre-wrap rounded-lg border bg-gray-50 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-300">
                      {g.description}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">No description provided.</p>
                  )}
                  <GrievanceAttachmentChips attachments={g.attachments} className="mt-3" />
                </section>

                <section>
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Linked {snapshot?.label || 'service'}
                  </h3>
                  <GrievanceServiceSnapshotPanel snapshot={snapshot} />
                </section>
              </>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
};

const GrievanceViewSidebar: React.FC<{
  g: GrievanceListRow & { resolvedAt?: string | null };
  user: GrievanceDetailPayload['user'];
  vendor: GrievanceDetailPayload['vendor'];
}> = ({ g, user, vendor }) => (
  <div className="grid grid-cols-1 gap-3 text-sm">
    <div>
      <span className="text-gray-500 dark:text-gray-400">Category</span>
      <p className="font-medium text-gray-900 dark:text-white">{g.category}</p>
    </div>
    <div>
      <span className="text-gray-500 dark:text-gray-400">Priority</span>
      <p className="font-medium text-gray-900 dark:text-white">{g.priority}</p>
    </div>
    <div>
      <span className="text-gray-500 dark:text-gray-400">Service</span>
      <p className="font-medium text-gray-900 dark:text-white">
        {g.serviceType} · <span className="font-mono text-xs">{g.serviceRefId}</span>
      </p>
    </div>
    <div>
      <span className="text-gray-500 dark:text-gray-400">Gross amount</span>
      <p className="font-semibold tabular-nums text-gray-900 dark:text-white">
        {formatInr(g.totalBookingAmount)}
      </p>
    </div>
    {g.refundAmount != null && Number(g.refundAmount) > 0 && (
      <div>
        <span className="text-gray-500 dark:text-gray-400">Refund issued</span>
        <p className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
          {formatInr(g.refundAmount)}
        </p>
      </div>
    )}
    <div>
      <span className="text-gray-500 dark:text-gray-400">User</span>
      <p className="font-medium text-gray-900 dark:text-white">
        {user?.name || g.userName || g.userId}
      </p>
      {user?.phone_number && <p className="text-xs text-gray-500">{user.phone_number}</p>}
    </div>
    {vendor && (
      <div>
        <span className="text-gray-500 dark:text-gray-400">Vendor</span>
        <p className="break-all font-medium text-gray-900 dark:text-white">
          {vendor.email || vendor.vendorId}
        </p>
      </div>
    )}
    <div>
      <span className="text-gray-500 dark:text-gray-400">Raised</span>
      <p className="text-gray-900 dark:text-white">{new Date(g.createdAt).toLocaleString()}</p>
    </div>
    {g.resolvedAt && (
      <div>
        <span className="text-gray-500 dark:text-gray-400">Resolved at</span>
        <p className="text-gray-900 dark:text-white">{new Date(g.resolvedAt).toLocaleString()}</p>
      </div>
    )}
  </div>
);
